import type { Middleware } from '@reduxjs/toolkit';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  COMPLETE_ONBOARDING,
  HIDE_ALL_MODALS,
  SET_TUTORIAL_VERSION_COMPLETE,
  SHOW_POST_ONBOARDING_APP_TOUR,
  SKIP_ONBOARDING,
} from '@reduxLocal/actions/types';
import {
  POST_ONBOARDING_APP_TOUR_DONE_KEY,
  POST_ONBOARDING_TOUR_OPEN_DELAY_MS,
} from '@components/modals/PostOnboardingAppTour/constants';
import { ONBOARDING_TUTORIAL_ID } from '@components/modals/PostOnboardingAppTour/resolvePendingOnboardingStep';
import { trackActivation } from '@utils/conversionAnalytics';

/**
 * After onboarding completes or is skipped in Redux, queue the post-onboarding app tour here so it
 * still runs if EasyModeScreen unmounts (e.g. React Strict Mode) or before layout effects observe the transition.
 * Waits {@link POST_ONBOARDING_TOUR_OPEN_DELAY_MS}, then dispatches `HIDE_ALL_MODALS` and the tour
 * so other RN modals / layout can settle (iOS presentation safety).
 */
export const postOnboardingTourMiddleware: Middleware =
  (store) => (next) => (action: { type?: string }) => {
    const pre = store.getState();
    const wasOnboarding = pre.UI?.onboardingInProgress === true;
    const shouldQueue =
      (action?.type === COMPLETE_ONBOARDING || action?.type === SKIP_ONBOARDING) &&
      wasOnboarding;

    const result = next(action);

    if (action?.type === COMPLETE_ONBOARDING && wasOnboarding) {
      void trackActivation({ source: 'onboarding_complete' });
    }

    if (!shouldQueue) {
      return result;
    }

    // Persist "onboarding finished" so a cold start (UI slice is not persisted) does not
    // re-enter onboarding or skip straight past the app tour.
    if (pre.promiseData?.isAuthenticated && pre.promiseData?.data?.userData) {
      store.dispatch({
        type: SET_TUTORIAL_VERSION_COMPLETE,
        payload: { tutorialId: ONBOARDING_TUTORIAL_ID, versionCode: 1 },
      });
      void import('@api/api')
        .then(({ completeTutorial }) => completeTutorial(ONBOARDING_TUTORIAL_ID, 1))
        .catch((error) => {
          console.warn('Failed to mark onboarding complete on backend', error);
        });
    }

    queueMicrotask(() => {
      void (async () => {
        try {
          if ((await AsyncStorage.getItem(POST_ONBOARDING_APP_TOUR_DONE_KEY)) === '1') {
            return;
          }
        } catch {
          /* show if storage unreadable */
        }
        if (!store.getState().promiseData?.isAuthenticated) {
          return;
        }
        setTimeout(() => {
          if (!store.getState().promiseData?.isAuthenticated) {
            return;
          }
          // Clear every Redux-driven modal/sheet first so the tour RN `Modal` is not competing
          // with plan picker, auth sheets, message chrome, etc. (`SHOW_POST_ONBOARDING` runs next).
          store.dispatch({ type: HIDE_ALL_MODALS });
          store.dispatch({ type: SHOW_POST_ONBOARDING_APP_TOUR });
        }, POST_ONBOARDING_TOUR_OPEN_DELAY_MS);
      })();
    });

    return result;
  };
